import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/useAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";
import { AgentIconPicker } from "@/features/agents/components/AgentIconPicker";
import { useTeams } from "@/features/teams/hooks/useTeams";

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
  isNew: boolean;
}

/**
 * Slugify: normaliza acentos (NFD + remove combining marks U+0300..U+036F)
 * antes de baixar para lowercase. Isso garante que "Jurídica" -> "juridica"
 * (e nao "jur-dica" como acontecia quando o range estava com literais
 * combining char invalidos no arquivo).
 */
function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

export function IdentityTab({ draft, onChange, isNew }: Props) {
  const { t } = useTranslation("agents");
  const teamsQuery = useTeams();
  const { isAdmin, memberships } = useAuth();

  // Non-admins so podem criar agentes para times onde sao creator OR
  // manager (admins veem tudo). Filtra a lista exibida no Select.
  const eligibleTeams = useMemo(() => {
    const all = teamsQuery.data ?? [];
    if (isAdmin) return all;
    const eligibleIds = new Set(
      memberships
        .filter((m) => m.role === "creator" || m.role === "manager")
        .map((m) => m.team.id),
    );
    return all.filter((t) => eligibleIds.has(t.id));
  }, [teamsQuery.data, isAdmin, memberships]);

  const autoSlug = useMemo(() => slugify(draft.name), [draft.name]);
  const slugIsEmpty = !draft.slug;
  const showAutoSlug = slugIsEmpty && draft.name;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="agent-name">{t("identity.name")} *</Label>
          <Input
            id="agent-name"
            value={draft.name}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder={t("identity.namePlaceholder")}
            maxLength={255}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="agent-slug">{t("identity.slug")}</Label>
          <Input
            id="agent-slug"
            value={draft.slug}
            onChange={(e) => onChange({ ...draft, slug: e.target.value })}
            placeholder={showAutoSlug ? autoSlug : t("identity.slugPlaceholder")}
            maxLength={255}
          />
          {showAutoSlug ? (
            <p className="text-xs text-muted-foreground">
              {t("identity.slugHintWithSuggestion", { suggestion: autoSlug })}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t("identity.slugHint")}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="agent-description">{t("identity.description")}</Label>
        <Textarea
          id="agent-description"
          value={draft.description}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
          rows={3}
          placeholder={t("identity.descriptionPlaceholder")}
        />
      </div>

      <div className="space-y-2">
        <Label>{t("identity.icon")}</Label>
        <AgentIconPicker
          value={draft.icon}
          onChange={(name) => onChange({ ...draft, icon: name })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="agent-owner-team">{t("identity.ownerTeam")} *</Label>
        {isNew ? (
          <Select
            value={draft.owner_team_id || ""}
            onValueChange={(v) => onChange({ ...draft, owner_team_id: v })}
          >
            <SelectTrigger id="agent-owner-team">
              <SelectValue placeholder={t("identity.ownerTeamPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {eligibleTeams.length === 0 ? (
                <SelectItem value="__none__" disabled>
                  {t("identity.ownerTeamNoEligible")}
                </SelectItem>
              ) : (
                eligibleTeams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        ) : (
          <>
            <Input
              id="agent-owner-team"
              value={
                teamsQuery.data?.find((t) => t.id === draft.owner_team_id)
                  ?.name ?? draft.owner_team_id
              }
              disabled
              readOnly
            />
            <p className="text-xs text-muted-foreground">
              {t("identity.ownerTeamReadonlyHint")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
